import type { EventTriggerPopupData } from '../stores/event-trigger/types';

import { isLimitedOfferBenefit } from './events';

// ---- frozen. ops never sees or sets these ----
const K = 2.2; // front-load
const ALPHA = 0.7; // share draining at a steady rate — the late floor
const MAX_SLOPE = 0.65; // irregularity; must stay < 1 or the counter can tick upward
const EPSILON = 1e-9;
const PERIODS_SEC: ReadonlyArray<readonly [number, number]> = [
  [45, 1.0],
  [120, 0.6],
  [420, 0.35],
];
const SUMW = PERIODS_SEC.reduce((a, [, w]) => a + w, 0);

const hash = (s: string): number => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const warpCache = new Map<string, (p: number) => number>();

const getWarp = (offerKey: string, windowMin: number) => {
  const id = `${offerKey}|${windowMin}`;
  const hit = warpCache.get(id);
  if (hit) return hit;

  const waves = PERIODS_SEC.map(([periodSec, w], i) => ({
    f: (windowMin * 60) / periodSec,
    w,
    ph: ((hash(`${offerKey}:${i}`) % 1000) / 1000) * 2 * Math.PI,
  }));
  const s = waves.reduce((a, { f, w }) => a + w * 2 * Math.PI * f, 0) / SUMW;
  const scale = MAX_SLOPE / (Math.PI + s);

  const fn = (p: number) => {
    const env = Math.pow(4 * p * (1 - p), 0.25);
    let wv = 0;
    for (const { f, w, ph } of waves) wv += w * Math.sin(2 * Math.PI * f * p + ph);
    return p + scale * env * (wv / SUMW);
  };
  warpCache.set(id, fn);
  return fn;
};

const shape = (p: number) => (1 - ALPHA) * Math.pow(1 - p, K) + ALPHA * (1 - p);

/**
 * Simulated stock for a Limited Offer — display-only, gates nothing (the server decides
 * whether a purchase succeeds). Deterministic in its inputs, never random: two players who
 * have bought nothing see the same number at the same moment.
 *
 * `EPSILON` is load-bearing: at landmark values `displayCap * shape(q)` lands a hair above a
 * whole number in IEEE 754 (`243.00000000000003`) and a bare `ceil` returns one too many.
 * Zero is reached only via the `progress >= 1` branch, so the `max(1, …)` clamp cannot mask a
 * real sell-out.
 */
export const stockLeft = (
  offerKey: string,
  displayCap: number,
  startMs: number,
  soldOutMs: number,
  nowMs: number,
  purchases = 0, // this player's purchases of this offer
): number => {
  const progress = (nowMs - startMs) / (soldOutMs - startMs);
  if (progress <= 0) return Math.max(1, displayCap - purchases);
  if (progress >= 1) return 0; // caller renders SOLD OUT
  const windowMin = (soldOutMs - startMs) / 60_000;
  const q = Math.min(1, Math.max(0, getWarp(offerKey, windowMin)(progress)));
  const onCurve = Math.ceil(displayCap * shape(q) - EPSILON);
  return Math.max(1, onCurve - purchases);
};

export type LimitedOfferConfig = {
  offerKey: string;
  displayCap: number;
  startMs: number;
  soldOutMs: number;
};

export type LimitedOfferStock = {
  stock: number;
  isSoldOut: boolean;
};

/**
 * Mirrors admin-frontend's `formatStockCounterText`: ops stores the label on the claim button
 * (`additionalData.stockText`) and the count is always prepended at render time.
 */
export const formatStockText = (label: string | undefined, stock: number): string =>
  [String(stock), label?.trim()].filter(Boolean).join(' ');

/**
 * What the claim button's stock line shows at `nowMs`: the number, or the sold-out label in its
 * place. `stock` is 0 whenever `isSoldOut` is true — the two can never disagree.
 */
export const getLimitedOfferStock = (config: LimitedOfferConfig, nowMs: number, purchases = 0): LimitedOfferStock => ({
  stock: stockLeft(config.offerKey, config.displayCap, config.startMs, config.soldOutMs, nowMs, purchases),
  isSoldOut: nowMs >= config.soldOutMs,
});

/**
 * The inputs `stockLeft` needs, or null when this event is not a limited offer or ops left the
 * configuration incomplete. `displayCap` and `soldOutAt` live on the event trigger's `extra`
 * (§3.2) because one offer group can be driven by several triggers with different windows.
 */
export const getLimitedOfferConfig = (event?: EventTriggerPopupData | null): LimitedOfferConfig | null => {
  if (!event || !isLimitedOfferBenefit(event.benefit)) return null;

  const { limitedOffer } = event.eventTrigger.extra;
  const startMs = new Date(event.eventTrigger.startDate ?? '').getTime();
  const soldOutMs = new Date(limitedOffer?.soldOutAt ?? '').getTime();
  const displayCap = limitedOffer?.displayCap ?? 0;

  if (!Number.isFinite(startMs) || !Number.isFinite(soldOutMs) || soldOutMs <= startMs || displayCap <= 0) {
    return null;
  }

  return { offerKey: event.benefit.key, displayCap, startMs, soldOutMs };
};

/**
 * What this player sees at `nowMs`: the simulated stock less their own purchases, spent once that
 * reaches zero. Null for any event that is not a limited offer, or whose configuration ops left
 * incomplete.
 *
 * The one definition of the number on screen, so anything reporting it — the counter, the purchase
 * gates, analytics — cannot drift from what the player was shown.
 *
 * `stockLeft`'s own `purchases` argument is deliberately not used: it floors at 1, so the curve never
 * reads zero before soldOutAt. Right for the population's stock, wrong for a player who has already
 * taken their share.
 */
export const getPlayerLimitedOfferStock = (
  event?: EventTriggerPopupData | null,
  nowMs: number = Date.now(),
): LimitedOfferStock | null => {
  const config = getLimitedOfferConfig(event);
  if (!config || !event) return null;

  const { stock, isSoldOut } = getLimitedOfferStock(config, nowMs);
  const playerStock = Math.max(stock - (event.used ?? 0), 0);

  return { stock: playerStock, isSoldOut: playerStock === 0 || isSoldOut };
};
